import { createSelector } from 'reselect'
import getColorScale from '@signal/utils/getColorScale'
import {
  createThemeSelector,
  createVariableThemeSelectorCreator
} from './utils'
import {
  paletteCommonSelector,
  palettePrimarySelector,
  paletteSecondarySelector,
  paletteGreySelector,
  paletteTextSelector,
  paletteBackgroundSelector,
  paletteActionSelector,
  paletteSuccessSelector,
  paletteErrorSelector,
  paletteWarnSelector
} from './palette'

// Color selectors
export const colorBlackSelector = createSelector(
  paletteCommonSelector,
  common => common.black
)

export const colorWhiteSelector = createSelector(
  paletteCommonSelector,
  common => common.white
)

/**
 * Given a palette theme selector, and optionally a default value to use as `hue`,
 * this util uses {@link createVariableThemeSelectorCreator} to create a variable
 * theme selector creator. The returned selector creator takes in a `hue` value
 * and returns a selector that will fetch the proper hue from the palette using
 * the palette selector provided initially.
 * @param {ReduxSelector} paletteSelector - theme selector for a palette
 * @param {String} defaultHue - default hue to use
 * @return {VariableThemeSelectorCreator}
 */
export const createVariableColorSelectorCreator = (
  paletteSelector,
  defaultHue = 'main'
) =>
  createVariableThemeSelectorCreator(
    // selector creator
    (hue = defaultHue) =>
      createThemeSelector(paletteSelector, (palette = {}) => palette[hue]),
    // generate cache key (must account for default value)
    (hue = defaultHue) => hue
  )

// Variable color selector getters
export const getColorPrimarySelector = createVariableColorSelectorCreator(
  palettePrimarySelector,
  'main'
)
export const getColorSecondarySelector = createVariableColorSelectorCreator(
  paletteSecondarySelector,
  'main'
)
export const getColorSecondaryLightSelector = createVariableColorSelectorCreator(
  paletteSecondarySelector,
  'light'
)

export const getColorPrimaryDarkSelector = createVariableColorSelectorCreator(
  palettePrimarySelector,
  'dark'
)
export const getColorPrimaryLightSelector = createVariableColorSelectorCreator(
  palettePrimarySelector,
  'light'
)
// The `hue` value provided to `getColorGreySelector` can be the name of a hue,
// or optionally a grey variant value (e.g. 400)
export const getColorGreySelector = createVariableColorSelectorCreator(
  paletteGreySelector,
  'main'
)
export const getColorTextSelector = createVariableColorSelectorCreator(
  paletteTextSelector,
  'primary'
)
export const getColorBackgroundSelector = createVariableColorSelectorCreator(
  paletteBackgroundSelector,
  'default'
)
export const getColorActionSelector = createVariableColorSelectorCreator(
  paletteActionSelector,
  'active'
)
export const getColorSuccessSelector = createVariableColorSelectorCreator(
  paletteSuccessSelector,
  'main'
)
export const getColorErrorSelector = createVariableColorSelectorCreator(
  paletteErrorSelector,
  'main'
)
export const getColorWarnSelector = createVariableColorSelectorCreator(
  paletteWarnSelector,
  'main'
)

export const getColorScalePrimarySelector = i => theme => {
  const primaryColor = getColorPrimarySelector()(theme)
  const colorScale = getColorScale(primaryColor)
  return colorScale[i]
}
